import { domainCodeAtom, navBarOpenAtom } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { GridColumnVisibilityModel, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { NotRenewingContext } from '@/App';
import { getQuickFilterListPreview } from '@/apis/quickFilter';
import { environment } from '@/config';
import { QFLCPreviewData, QFilter } from '@/types';
import { createBaseContactUrl, createRocketUrlForContact } from '@/util';
import { SearchMetadata } from '@components/SaveSearchDialog';
import { useSearchPreviewsPaused } from '@/hooks/useSearchPreviewsPaused';
import { useUnsavedChangesBlocker } from '@hooks/useUnsavedChangesBlocker';
import { useUserDetails } from '@hooks/useUserDetails';
import { useSentryTags } from '@/hooks/useSentryTags';
import useFilterCriteria, {
  DEFAULT_DRAWER_NAME,
  DispatchFilterCriteria,
  FilterCriteria,
} from './filterCriteriaReducer';
import { buildQFilter } from './util';

const DEFAULT_PAGE_SIZE = 10;

export type QuickFilterListCreatorPageProps = {
  loadedFilterCriteria: FilterCriteria;
  loadedMetadata: SearchMetadata;
  loadedAccordionStates: Record<string, boolean>;
  missingFilters: string[];
};

export function useQuickFilterListCreatorPage({
  loadedFilterCriteria,
  loadedMetadata,
  loadedAccordionStates,
  missingFilters,
}: QuickFilterListCreatorPageProps) {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const { data: userDetails } = useUserDetails();
  const { searchId } = useParams();
  const { setNotRenewingPresent } = useContext(NotRenewingContext);

  useSentryTags({ 'search-type': 'quick', 'search-id': searchId });

  const [accordionStates, setAccordionStates] = useState(loadedAccordionStates);
  const [filterCriteria, dispatchFilterCriteria] = useFilterCriteria(loadedFilterCriteria);
  const [openFilterWarning, setOpenFilterWarning] = useState(missingFilters.length > 0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [opendrawerLeft, setopendrawerLeft] = useState(true);
  const [rightDrawIsOpen, setRightDrawerIsOpen] = useState(false);
  const [opendrawerLeftCache, setopendrawerLeftCache] = useState(true);
  const [isSaveListDialogOpen, setIsSaveListDialogOpen] = useState(false);

  const [enablePicture, setEnablePicture] = useState(true);
  const [filterChangedSinceLastPreview, setFilterChangedSinceLastPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isDisableSaveButton, setIsDisableSaveButton] = useState(true);

  const [saveSearchDialogStatus, setSaveSearchDialogStatus] = useState<
    'open' | 'closed' | 'prefilled'
  >('closed');
  const [isUpdateSearchDialogOpen, setIsUpdateSearchDialogOpen] = useState(false);

  const [isSearchModified, setIsSearchModified] = useState(false);
  const { blocker, popStateBlock, allowNextNavigation } =
    useUnsavedChangesBlocker(isSearchModified);
  const [searchMetadata, setSearchMetadata] = useState<SearchMetadata>(loadedMetadata);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  const dispatchActionAndClearDrawerText: DispatchFilterCriteria = (dispatchVal) => {
    dispatchFilterCriteria(dispatchVal);
    setIsSearchModified(true);
    setIsDisableSaveButton(true);

    if (dispatchVal.type === 'setMemberships' && dispatchVal.payload.length === 0) {
      setNotRenewingPresent(false);
    } else if (dispatchVal.type === 'setNotRenewing') {
      if (dispatchVal.payload) {
        setNotRenewingPresent(true);
      } else {
        setNotRenewingPresent(false);
      }
    }
  };

  const [contactIds, setContactIds] = useState<number[]>([]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mark preview stale when filter criteria change
    setFilterChangedSinceLastPreview(true);
  }, [filterCriteria]);

  const handleOtherActions = () => {
    setopendrawerLeftCache(opendrawerLeft);
    setopendrawerLeft(false);
    setRightDrawerIsOpen(true);
  };

  const handleOtherActionsClose = () => {
    setRightDrawerIsOpen(false);
    setopendrawerLeft(opendrawerLeftCache);
  };

  const handleSaveToListDialogOpen = () => {
    setIsSaveListDialogOpen(true);
  };

  const handleSaveToListDialogClose = () => {
    setIsSaveListDialogOpen(false);
  };

  const handleSaveSearchDialogOpen = () => {
    if (isSearchModified)
      if (searchMetadata.id) setIsUpdateSearchDialogOpen(true);
      else setSaveSearchDialogStatus('open');
    else setSaveSearchDialogStatus('prefilled');
  };

  const handleSaveSearchDialogClose = (newMetadata?: SearchMetadata) => {
    if (newMetadata) {
      setSearchMetadata(newMetadata);
      setIsSearchModified(false);
    }
    setSaveSearchDialogStatus('closed');
  };

  const [searchQFilter, setSearchQFilter] = useState<QFilter>({});
  const previewsPaused = useSearchPreviewsPaused();

  const {
    data: listPreview,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['preview', searchQFilter],
    queryFn: async () => {
      if (Object.keys(searchQFilter).length === 0) {
        return { count: 0, entities: [] };
      }
      const token = await getAccessTokenSilently();
      const returnedData = (await getQuickFilterListPreview(
        token,
        searchQFilter,
        domainCode
      )) as QFLCPreviewData;
      return returnedData;
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
    gcTime: 0,
    enabled: !previewsPaused,
  });

  const handleApplyFilter = async () => {
    if (previewsPaused) return;
    const newSearchQFilter = buildQFilter(filterCriteria);
    if (Object.keys(newSearchQFilter).length > 0) {
      setSearchQFilter(newSearchQFilter);
      refetch();
      setEnablePicture(false);
      setFilterChangedSinceLastPreview(false);
      setIsDisableSaveButton(false);
    } else {
      setEnablePicture(true);
    }
    setContactIds([]);
  };

  const nextQfilter = buildQFilter(filterCriteria);

  const isDisableApply =
    previewsPaused ||
    Object.keys(nextQfilter).length === 0 ||
    (filterCriteria.issincespecificelection === true &&
      (filterCriteria.taskelections ?? []).length !== 1);

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const setIsNavbarOpen = useSetAtom(navBarOpenAtom);

  useEffect(() => {
    setIsNavbarOpen(false);
  }, [setIsNavbarOpen]);

  useEffect(() => {
    if (isFetching === false) {
      const returnedDataIds = listPreview ? listPreview.entities.map((x) => x.id) : [];
      const filteredContactIds = contactIds.filter((value) => returnedDataIds.includes(value));
      // eslint-disable-next-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-derived-state -- drop selected ids that are no longer in the preview
      setContactIds(filteredContactIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  const isDev = ['test', 'local', 'dev'].includes(environment);
  const baseContactUrl = createBaseContactUrl(domainCode ?? 'ag', isDev);
  const contactIdUrl = useCallback(
    (id?: number) => createRocketUrlForContact(baseContactUrl, id),
    [baseContactUrl]
  );

  const disableSelectAll = sortModel.length > 0 || filterModel.items.length > 0;
  const drawerTitle = `${searchMetadata.name ?? DEFAULT_DRAWER_NAME}`;

  return {
    accordionStates,
    setAccordionStates,
    filterCriteria,
    dispatchActionAndClearDrawerText,
    isDisableApply,
    isDisableSaveButton,
    handleApplyFilter,
    handleSaveSearchDialogOpen,
    isFetching,
    opendrawerLeft,
    setopendrawerLeft,
    drawerTitle,
    isSearchModified,
    enablePicture,
    listPreview,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    columnVisibilityModel,
    setColumnVisibilityModel,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    contactIds,
    setContactIds,
    contactIdUrl,
    disableSelectAll,
    userDetails,
    handleOtherActions,
    rightDrawIsOpen,
    filterChangedSinceLastPreview,
    handleSaveToListDialogOpen,
    isSaveListDialogOpen,
    handleSaveToListDialogClose,
    searchQFilter,
    saveSearchDialogStatus,
    setSaveSearchDialogStatus,
    handleSaveSearchDialogClose,
    searchMetadata,
    isUpdateSearchDialogOpen,
    setIsUpdateSearchDialogOpen,
    setIsSearchModified,
    openFilterWarning,
    missingFilters,
    setOpenFilterWarning,
    blocker,
    popStateBlock,
    allowNextNavigation,
    handleOtherActionsClose,
  };
}
